import type { KeyboardEvent, ReactNode } from 'react';
import { stepperNext } from './numeric';
import type { WidgetProps } from './types';

/**
 * Compteur `stepper` (− / +) d'une mesure (ADR-146 §6.1), comme la tablette : bornes dures
 * `validation.min` / `max` du champ seulement, jamais la cible — une mesure hors cible reste saisissable
 * (la ligne de cible la signale, la non-conformité se pose à la validation). Un pas (déclaré, `presentation.step` 1.9.0, sinon 1) à partir de la
 * valeur, ou de 0 au premier appui, ramené dans les bornes ; un bouton qui ne bougerait rien se grise.
 * Flèches haut et bas du clavier comme les boutons ; une valeur tapée est gardée telle quelle.
 */
export function Stepper({ id, field, value, onChange, disabled, invalid, describedBy, validation, unit, step }: WidgetProps): ReactNode {
  if (field.type !== 'number' && field.type !== 'decimal' && field.type !== 'integer') return null;
  const min = validation?.min;
  const max = validation?.max;
  const current = typeof value === 'number' && Number.isFinite(value) ? value : null;
  const nextOf = (delta: 1 | -1): number => stepperNext(current, delta, step, min, max);
  const moves = (delta: 1 | -1): boolean => nextOf(delta) !== current;
  const move = (delta: 1 | -1): void => {
    if (moves(delta)) onChange(nextOf(delta));
  };
  const atMin = !moves(-1);
  const atMax = !moves(1);
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(-1);
    }
  };
  const input = (
    <input
      id={id}
      type="number"
      inputMode="decimal"
      step="any"
      {...(min === undefined ? {} : { min })}
      {...(max === undefined ? {} : { max })}
      required={field.required === true}
      disabled={disabled}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      value={current ?? ''}
      onKeyDown={onKeyDown}
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
    />
  );
  return (
    <span className="ay-stepper">
      <button type="button" className="ay-button ay-button--secondary ay-stepper__button" aria-label="Diminuer" aria-controls={id} disabled={disabled || atMin} onClick={() => move(-1)}>
        −
      </button>
      {unit === undefined || unit === '' ? (
        input
      ) : (
        <span className="ay-field__suffixed ay-stepper__value">
          {input}
          <span className="ay-field__suffix" aria-hidden="true">
            {unit}
          </span>
        </span>
      )}
      <button type="button" className="ay-button ay-button--secondary ay-stepper__button" aria-label="Augmenter" aria-controls={id} disabled={disabled || atMax} onClick={() => move(1)}>
        +
      </button>
    </span>
  );
}
